import { Box3, Quaternion, Vector3 } from 'three';

import {
    convertToDegree,
    convertToRadiant,
    updateFullQuaternion
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { LengthUnit } from 'app/modules/wallforming/components/scenes/types/lengthUnit';
import { EPSILON, FILLER_ARTICLE_NUMBER } from 'shared/constants';

import type { ArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleInstance';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

/* Transformation utils */
const rotRollAxis = new Vector3();
const fillerGeometryBounds = new Box3();
const fillerGeometrySlotBounds = new Box3();
const fillerGeometrySize = new Vector3();

export const lockedTranslationDir = new Vector3();
export const deltaVector = new Vector3();

export const snappingPosition = new Vector3();
export const newPosition = new Vector3();
export const prevPosition = new Vector3();

export const rotDeltaQuat = new Quaternion();
export const rotPartQuat = new Quaternion();
export const rotNewAlignQuat = new Quaternion();
export const rotRollQuat = new Quaternion();
export const rotNewAlignment = new Vector3();

export function decomposeRollAroundAxis(q: Quaternion, axis: Vector3): number {
    const sinHalf = Math.sqrt(q.x * q.x + q.y * q.y + q.z * q.z);
    if (sinHalf < EPSILON) {
        return 0;
    }

    rotRollAxis.set(q.x / sinHalf, q.y / sinHalf, q.z / sinHalf);
    let angle = 2 * Math.atan2(sinHalf, q.w);
    if (angle > Math.PI) {
        angle -= 2 * Math.PI;
    }
    if (rotRollAxis.dot(axis) < 0) {
        angle = -angle;
    }

    return angle;
}

export function updateInstanceMatricesForSelection(
    selectedInstanceIds: string[],
    state: PlacementState,
    updateColliders = true
) {
    const grouped = new Map<string, string[]>();
    for (const instanceId of selectedInstanceIds) {
        const dragRef = state.getSelectedArticleDragReference(instanceId);
        if (!dragRef?.articleRef.current) {
            continue;
        }

        const articleId = dragRef.articleRef.current.id;
        const list = grouped.get(articleId) ?? [];
        list.push(instanceId);
        grouped.set(articleId, list);
    }

    for (const [articleId, instanceIds] of grouped.entries()) {
        const dragRef = state.articleDragRefsMap.current.get(articleId);
        if (!dragRef) {
            continue;
        }
        state.updateInstanceMatrices(true, dragRef.articleRef, updateColliders, instanceIds);
    }
}

export function collectUpdatedSelectedInstances(selectedInstanceIds: string[], state: PlacementState) {
    const updatedInstances: Array<{ articleId: string; instanceId: string; instance: ArticleInstance }> = [];

    for (const instanceId of selectedInstanceIds) {
        const dragRef = state.getSelectedArticleDragReference(instanceId);
        if (!dragRef?.articleRef.current?.articleInstances) {
            continue;
        }

        const articleId = dragRef.articleRef.current.id;
        const instance = dragRef.articleRef.current.articleInstances[instanceId];
        if (!instance) {
            continue;
        }

        updatedInstances.push({ articleId, instanceId, instance });
    }

    return updatedInstances;
}

export function isFillerInstanceSelection(selectedInstanceIds: string[], state: PlacementState): boolean {
    return (
        selectedInstanceIds.length > 0 &&
        selectedInstanceIds.every((instanceId) => {
            const articleRef = state.getSelectedArticleDragReference(instanceId)?.articleRef.current;
            return articleRef?.articleNumber === FILLER_ARTICLE_NUMBER;
        })
    );
}

export function isSingleFillerInstanceSelection(selectedInstanceIds: string[], state: PlacementState): boolean {
    return selectedInstanceIds.length === 1 && isFillerInstanceSelection(selectedInstanceIds, state);
}

export function getSelectedScaleFactor(axis: 0 | 1 | 2, selectedInstanceIds: string[], state: PlacementState): number {
    for (const instanceId of selectedInstanceIds) {
        const dragRef = state.getSelectedArticleDragReference(instanceId);
        const instance = dragRef?.articleRef.current?.articleInstances[instanceId];
        const part = instance ? Object.values(instance.parts)[0] : undefined;
        if (part) {
            return Math.abs(part.scale.getComponent(axis));
        }
    }

    return 1;
}

export function getFillerDimensionPerScaleFactor(
    axis: 0 | 1 | 2,
    selectedInstanceIds: string[],
    state: PlacementState
): number | undefined {
    const instanceId = selectedInstanceIds[0];
    if (!instanceId) {
        return undefined;
    }

    const dragRef = state.getSelectedArticleDragReference(instanceId);
    const articleRef = dragRef?.articleRef.current;
    const firstPart = articleRef?.articleInstances[instanceId]
        ? Object.values(articleRef.articleInstances[instanceId].parts)[0]
        : undefined;
    if (!articleRef || !firstPart) {
        return undefined;
    }

    const geometrySlots = state.articleGeometrySlots.get(articleRef.id);
    const physicalBatch = state.batchedMeshes.physical;
    if (!geometrySlots || !physicalBatch || geometrySlots.physicalSlots.length === 0) {
        return undefined;
    }

    fillerGeometryBounds.makeEmpty();
    for (const { slotId } of geometrySlots.physicalSlots) {
        physicalBatch.getBoundingBoxAt(slotId, fillerGeometrySlotBounds);
        fillerGeometryBounds.union(fillerGeometrySlotBounds);
    }

    if (fillerGeometryBounds.isEmpty()) {
        return undefined;
    }

    const dimension = fillerGeometryBounds.getSize(fillerGeometrySize).getComponent(axis);
    return Number.isFinite(dimension) && dimension > 0 ? dimension : undefined;
}

export function clampScaleFactor(value: number): number {
    return Math.max(EPSILON, value);
}

export function applyTranslationToAllInstances(delta: Vector3, selectedInstanceIds: string[], state: PlacementState) {
    for (const instanceId of selectedInstanceIds) {
        const dragRef = state.getSelectedArticleDragReference(instanceId);
        if (!dragRef?.articleRef.current) {
            continue;
        }
        const articleId = dragRef.articleRef.current.id;
        const backupInstance = state.articles[articleId]?.articleInstances[instanceId];
        const liveInstance = dragRef.articleRef.current.articleInstances[instanceId];
        if (!backupInstance || !liveInstance) {
            continue;
        }

        for (const [partName, backupPart] of Object.entries(backupInstance.parts)) {
            const livePart = liveInstance.parts[partName];
            if (!livePart) {
                continue;
            }
            livePart.position.set(
                backupPart.position.x + delta.x,
                backupPart.position.y + delta.y,
                backupPart.position.z + delta.z
            );
        }
    }
}

export function getMovementLabel(direction: string, deltaMeters: number) {
    return parseFloat(`${direction}${deltaMeters.toFixed(3)}`);
}

export function applyScalingToAllInstances(
    axis: 0 | 1 | 2,
    factor: number,
    center: Vector3,
    direction: Vector3,
    selectedInstanceIds: string[],
    state: PlacementState
) {
    const offset = new Vector3();

    for (const instanceId of selectedInstanceIds) {
        const dragRef = state.getSelectedArticleDragReference(instanceId);
        if (!dragRef?.articleRef.current) {
            continue;
        }
        const articleId = dragRef.articleRef.current.id;
        const backupInstance = state.articles[articleId]?.articleInstances[instanceId];
        const liveInstance = dragRef.articleRef.current.articleInstances[instanceId];
        if (!backupInstance || !liveInstance) {
            continue;
        }

        for (const [partName, backupPart] of Object.entries(backupInstance.parts)) {
            const livePart = liveInstance.parts[partName];
            if (!livePart) {
                continue;
            }

            offset.subVectors(backupPart.position, center);
            livePart.position
                .copy(backupPart.position)
                .addScaledVector(direction, offset.dot(direction) * (factor - 1));
            livePart.scale.copy(backupPart.scale);
            livePart.scale.setComponent(axis, backupPart.scale.getComponent(axis) * factor);
        }
    }
}

export function applyRotationToAllInstances(
    deltaRotation: number,
    center: Vector3,
    normal: Vector3,
    selectedInstanceIds: string[],
    state: PlacementState
) {
    rotDeltaQuat.setFromAxisAngle(normal, deltaRotation);

    for (const instanceId of selectedInstanceIds) {
        const dragRef = state.getSelectedArticleDragReference(instanceId);
        if (!dragRef?.articleRef.current) {
            continue;
        }
        const articleId = dragRef.articleRef.current.id;
        const backupInstance = state.articles[articleId]?.articleInstances[instanceId];
        const currentInstance = dragRef.articleRef.current.articleInstances[instanceId];
        if (!backupInstance || !currentInstance) {
            continue;
        }

        for (const [partName, backupPart] of Object.entries(backupInstance.parts)) {
            const part = currentInstance.parts[partName];
            if (!part) {
                continue;
            }

            // rotate position around center (rigid body orbit)
            const relPos = new Vector3().copy(backupPart.position).sub(center);
            relPos.applyAxisAngle(normal, deltaRotation);
            part.position.copy(center).add(relPos);

            updateFullQuaternion(backupPart.alignment, backupPart.rotation, rotPartQuat);

            rotPartQuat.premultiply(rotDeltaQuat);
            rotNewAlignment.copy(backupPart.alignment).applyQuaternion(rotDeltaQuat);
            part.alignment.copy(rotNewAlignment);

            updateFullQuaternion(rotNewAlignment, 0, rotNewAlignQuat);

            rotRollQuat.copy(rotNewAlignQuat).invert();
            rotRollQuat.premultiply(rotPartQuat);
            part.rotation = decomposeRollAroundAxis(rotRollQuat, rotNewAlignment);
        }
    }
}

export function getSnappingRotation(radians: number) {
    let degrees = convertToDegree(radians);
    degrees = Math.round(degrees);
    if (degrees < 0) {
        degrees += 360;
    }

    return convertToRadiant(degrees);
}

// snaps to the nearest millimetre (0.001 m)
export function updateSnappingPosition(hasLockedTranslationDir: boolean) {
    deltaVector.subVectors(newPosition, prevPosition);

    if (hasLockedTranslationDir) {
        const projected = deltaVector.dot(lockedTranslationDir);
        deltaVector.copy(lockedTranslationDir).multiplyScalar(projected);
    }

    snappingPosition.set(
        prevPosition.x + Math.round(deltaVector.x * 1000) / 1000,
        prevPosition.y + Math.round(deltaVector.y * 1000) / 1000,
        prevPosition.z + Math.round(deltaVector.z * 1000) / 1000
    );
}

/* Transformation label */
const METERS_PER_UNIT: Record<LengthUnit, number> = {
    [LengthUnit.CENTIMETER]: 0.01,
    [LengthUnit.MILLIMETER]: 0.001,
    [LengthUnit.INCHES]: 0.0254,
    [LengthUnit.FEET]: 0.3048
};

export function toInternalValue(value: number, unit: LengthUnit): number {
    return value * METERS_PER_UNIT[unit];
}

export function toDisplayValue(value: number, unit: LengthUnit): number {
    return value / METERS_PER_UNIT[unit];
}

/* Index lookup cache */

export const indexLookupCache = new Map<string, { instanceIds: string[]; lookup: Map<string, number> }>();

export function createIndexLookup(articleId: string, instanceIds: string[]): Map<string, number> {
    const lookup = new Map(instanceIds.map((id, idx) => [id, idx]));
    indexLookupCache.set(articleId, { instanceIds: [...instanceIds], lookup });
    return lookup;
}
